import { useState } from 'react'
import {
  Newspaper,
  Tag,
  BookOpen,
  Bell,
  Mail,
  Send,
  Trash2,
  Check,
} from 'lucide-react'
import { Button, Input, Textarea, PageHeader, EmptyState, toast, cn, formatDate } from '@rr/ui'
import {
  useBroadcastHistory,
  useBroadcastAudienceCounts,
  useSendBroadcast,
  useDeleteBroadcast,
  type BroadcastType,
  type BroadcastAudience,
  type BroadcastChannel,
} from '@/modules/marketing/useBroadcasts'

const TYPES: {
  value: BroadcastType
  label: string
  description: string
  icon: typeof Newspaper
}[] = [
  { value: 'news', label: 'News', description: 'General announcements', icon: Newspaper },
  { value: 'discount', label: 'Discount', description: 'Promotions and offers', icon: Tag },
  { value: 'blog', label: 'Blog', description: 'Product updates and articles', icon: BookOpen },
]

const AUDIENCES: { value: BroadcastAudience; label: string }[] = [
  { value: 'all', label: 'All users' },
  { value: 'active', label: 'Active tenants' },
]

const TYPE_TONE: Record<BroadcastType, string> = {
  news: 'bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300',
  discount: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300',
  blog: 'bg-violet-100 text-violet-700 dark:bg-violet-500/15 dark:text-violet-300',
}

type View = 'compose' | 'history'

export default function BroadcastsPage() {
  const { data: history = [] } = useBroadcastHistory()
  const [view, setView] = useState<View>('compose')

  return (
    <div className="space-y-5">
      <PageHeader
        title="Broadcasts"
        subtitle="Send news, discounts and announcements to tenant users."
      />

      {/* Compose / History switch */}
      <div className="inline-flex rounded-[10px] border border-border bg-muted/40 p-0.5">
        {(['compose', 'history'] as View[]).map((v) => (
          <button
            key={v}
            type="button"
            onClick={() => setView(v)}
            className={cn(
              'rounded-[8px] px-3.5 py-1.5 text-[13px] font-medium capitalize transition-colors',
              view === v
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {v}
            {v === 'history' && history.length > 0 && (
              <span className="ml-1.5 rounded-full bg-muted px-1.5 text-[11px] tabular-nums text-muted-foreground">
                {history.length}
              </span>
            )}
          </button>
        ))}
      </div>

      {view === 'compose' ? <Compose /> : <History />}
    </div>
  )
}

// ── Compose ──────────────────────────────────────────────────────────────────
function Compose() {
  const { mutateAsync: send, isPending } = useSendBroadcast()
  const { data: counts } = useBroadcastAudienceCounts()

  const [type, setType] = useState<BroadcastType>('news')
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [audience, setAudience] = useState<BroadcastAudience>('all')
  // In-app is the only live channel in v1; email is dark-shipped and the
  // toggle below is disabled, so channels stays fixed at ['in_app'].
  const channels: BroadcastChannel[] = ['in_app']

  const audienceCount = counts?.[audience]
  const canSend =
    title.trim().length > 1 && body.trim().length > 0 && !isPending

  const handleSend = async () => {
    if (!canSend) return
    try {
      await send({ type, title: title.trim(), body: body.trim(), audience, channels })
      toast(
        audienceCount != null
          ? `Broadcast sent to ${audienceCount} users`
          : 'Broadcast sent',
        'success',
      )
      setTitle('')
      setBody('')
    } catch {
      toast('Failed to send broadcast', 'error')
    }
  }

  const channelLabel = 'In-app'

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_340px] lg:items-start">
      {/* Composer (70%) */}
      <div className="space-y-5 rounded-[16px] border border-border bg-card p-5 shadow-card">
        {/* Type — compact descriptive cards */}
        <Field label="Type">
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            {TYPES.map((tp) => {
              const active = type === tp.value
              return (
                <button
                  key={tp.value}
                  type="button"
                  onClick={() => setType(tp.value)}
                  aria-pressed={active}
                  className={cn(
                    'flex items-start gap-2.5 rounded-[12px] border p-2.5 text-left transition-all',
                    active
                      ? 'border-primary bg-primary/5 ring-1 ring-primary/20'
                      : 'border-border hover:border-primary/40 hover:bg-muted/40',
                  )}
                >
                  <tp.icon
                    className={cn(
                      'mt-0.5 h-4 w-4 shrink-0',
                      active ? 'text-primary' : 'text-muted-foreground',
                    )}
                  />
                  <span className="min-w-0">
                    <span className="block text-[13px] font-medium text-foreground">
                      {tp.label}
                    </span>
                    <span className="block text-[11px] leading-tight text-muted-foreground">
                      {tp.description}
                    </span>
                  </span>
                </button>
              )
            })}
          </div>
        </Field>

        {/* Title */}
        <Field label="Title">
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. 20% off all shipments this week"
          />
        </Field>

        {/* Message — the dominant element */}
        <Field label="Message">
          <Textarea
            rows={9}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Write the announcement users will receive…"
            className="min-h-[200px] resize-y"
          />
        </Field>

        {/* Audience — radio rows with check indicators */}
        <Field label="Audience">
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            {AUDIENCES.map((a) => {
              const active = audience === a.value
              return (
                <button
                  key={a.value}
                  type="button"
                  onClick={() => setAudience(a.value)}
                  aria-pressed={active}
                  className={cn(
                    'flex items-center gap-2.5 rounded-[10px] border px-3 py-2 text-left transition-all',
                    active
                      ? 'border-primary bg-primary/5 ring-1 ring-primary/20'
                      : 'border-border hover:border-primary/40 hover:bg-muted/40',
                  )}
                >
                  <span
                    className={cn(
                      'grid h-4 w-4 shrink-0 place-items-center rounded-full border',
                      active ? 'border-primary bg-primary text-primary-foreground' : 'border-border',
                    )}
                  >
                    {active && <Check className="h-3 w-3" />}
                  </span>
                  <span className="min-w-0">
                    <span className="block text-[13px] font-medium text-foreground">{a.label}</span>
                    <span className="text-[11px] tabular-nums text-muted-foreground">
                      {counts ? `${counts[a.value]} recipients` : '…'}
                    </span>
                  </span>
                </button>
              )
            })}
          </div>
        </Field>

        {/* Channels — in-app only in v1; email is dark-shipped. */}
        <Field label="Channels">
          <div className="flex gap-2">
            <ChannelToggle active icon={Bell} label="In-app" />
            <ChannelToggle
              active={false}
              disabled
              icon={Mail}
              label="Email"
              hint="Coming soon"
            />
          </div>
        </Field>
      </div>

      {/* Preview + CTA (30%, sticky) */}
      <div className="lg:sticky lg:top-4">
        <div className="rounded-[16px] border border-border bg-card p-5 shadow-card">
          <p className="mb-3 text-[12px] font-semibold uppercase tracking-wide text-muted-foreground">
            Broadcast preview
          </p>

          <div className="rounded-[12px] border border-border bg-background p-3">
            <span
              className={cn(
                'inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
                TYPE_TONE[type],
              )}
            >
              {type}
            </span>
            <p className="mt-2 text-[14px] font-semibold text-foreground">
              {title || 'Your title appears here'}
            </p>
            <p className="mt-1 whitespace-pre-wrap break-words text-[12.5px] text-muted-foreground">
              {body || 'Your message preview…'}
            </p>
          </div>

          <dl className="mt-4 space-y-2.5 text-[12.5px]">
            <SummaryRow label="Audience" value={AUDIENCES.find((a) => a.value === audience)!.label} />
            <SummaryRow label="Channels" value={channelLabel} />
            <SummaryRow
              label="Recipients"
              value={audienceCount != null ? `${audienceCount} users` : '…'}
              emphasize
            />
          </dl>

          <Button className="mt-4 w-full" onClick={handleSend} disabled={!canSend}>
            <Send className="h-4 w-4" />
            {isPending ? 'Sending…' : 'Send broadcast'}
          </Button>
          <p className="mt-2 text-center text-[11px] text-muted-foreground">
            Delivered as in-app notifications to the selected audience.
          </p>
        </div>
      </div>
    </div>
  )
}

// ── History ──────────────────────────────────────────────────────────────────
function History() {
  const { data: history = [], isLoading } = useBroadcastHistory()
  const { mutate: remove } = useDeleteBroadcast()

  if (isLoading && history.length === 0) {
    return (
      <div className="rounded-[16px] border border-border bg-card p-10 shadow-card">
        <p className="text-center text-[13px] text-muted-foreground">Loading…</p>
      </div>
    )
  }

  if (history.length === 0) {
    return (
      <div className="rounded-[16px] border border-border bg-card p-10 shadow-card">
        <EmptyState
          icon={Send}
          title="Nothing sent yet"
          description="Broadcasts you send will appear here."
        />
      </div>
    )
  }

  return (
    <div className="rounded-[16px] border border-border bg-card p-3 shadow-card">
      <ul className="divide-y divide-border">
        {history.map((b) => (
          <li key={b.id} className="group flex items-start gap-3 p-3">
            <span
              className={cn(
                'mt-0.5 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
                TYPE_TONE[b.type],
              )}
            >
              {b.type}
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-[13.5px] font-medium text-foreground">{b.title}</p>
              <p className="truncate text-[12px] text-muted-foreground">{b.body}</p>
              <p className="mt-1 flex flex-wrap items-center gap-x-2 text-[11px] text-muted-foreground">
                <span>{b.recipients} recipients</span>
                <span aria-hidden>·</span>
                <span>
                  {[b.channels.includes('in_app') && 'In-app', b.channels.includes('email') && 'Email']
                    .filter(Boolean)
                    .join(', ')}
                </span>
                <span aria-hidden>·</span>
                <span>{formatDate(b.sentAt)}</span>
              </p>
            </div>
            <button
              type="button"
              onClick={() => remove(b.id)}
              aria-label="Delete from history"
              className="shrink-0 rounded p-1 text-muted-foreground opacity-0 transition hover:text-destructive group-hover:opacity-100"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </li>
        ))}
      </ul>
    </div>
  )
}

// ── Bits ─────────────────────────────────────────────────────────────────────
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <label className="text-[12px] font-medium text-muted-foreground">{label}</label>
      {children}
    </div>
  )
}

function SummaryRow({
  label,
  value,
  emphasize,
}: {
  label: string
  value: string
  emphasize?: boolean
}) {
  return (
    <div className="flex items-center justify-between">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className={cn('font-medium text-foreground', emphasize && 'tabular-nums')}>{value}</dd>
    </div>
  )
}

function ChannelToggle({
  active,
  onClick,
  icon: Icon,
  label,
  disabled,
  hint,
}: {
  active: boolean
  onClick?: () => void
  icon: typeof Bell
  label: string
  disabled?: boolean
  hint?: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={active}
      title={hint}
      className={cn(
        'flex flex-1 items-center justify-center gap-2 rounded-[10px] border px-3 py-2 text-[13px] font-medium transition-all',
        disabled && 'cursor-not-allowed opacity-50',
        active
          ? 'border-primary bg-primary/5 text-foreground ring-1 ring-primary/20'
          : 'border-border text-muted-foreground hover:border-primary/40',
      )}
    >
      <Icon className={cn('h-4 w-4', active ? 'text-primary' : 'text-muted-foreground')} />
      {label}
      {hint && (
        <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-normal text-muted-foreground">
          {hint}
        </span>
      )}
    </button>
  )
}
